// A member's own note on one verse. Markdown, rendered by the same renderer as
// the commentary, so what a reader writes about a verse and what the site says
// about it read the same way.
import { parseRef, renderMarkdown, toDisplay } from "@bibleunleashed/shared";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";

export function NoteEditor({
  verseRef,
  initial,
  saving,
  onSave,
  onClose,
}: {
  /** Not `ref`: React treats that name specially, whatever it means here. */
  verseRef: string;
  initial: string;
  saving: boolean;
  onSave: (bodyMd: string) => void;
  onClose: () => void;
}) {
  const [body, setBody] = useState(initial);
  const area = useRef<HTMLTextAreaElement>(null);
  const display = (() => {
    try {
      return toDisplay(parseRef(verseRef));
    } catch {
      return verseRef;
    }
  })();

  useEffect(() => {
    area.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const dirty = body !== initial;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/25 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Your note on ${display}`}
    >
      <div className="w-full max-w-[560px] rounded-xl border border-rule bg-paper p-5 shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Icon name="note" className="text-ink-3" width="16" height="16" />
          <h2 className="font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">Your note</h2>
          <span className="font-serif text-[15px] font-semibold text-accent">{display}</span>
          <button onClick={onClose} aria-label="Close" className="ml-auto grid size-7 place-items-center rounded text-ink-3 hover:bg-paper-2 hover:text-ink">
            <Icon name="close" width="15" height="15" />
          </button>
        </div>

        <textarea
          ref={area}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={7}
          placeholder={`What do you want to remember about ${display}?`}
          aria-label={`Your note on ${display}`}
          className="mt-3 w-full resize-y rounded-md border border-rule bg-paper-2 px-3 py-2.5 font-serif text-[15.5px] leading-relaxed text-ink focus:border-accent focus-visible:outline-none"
        />

        {body.trim() && (
          <div className="mt-3 border-t border-rule pt-3">
            <p className="mb-1.5 font-sans text-[10.5px] tracking-[0.1em] text-ink-3 uppercase">Preview</p>
            <div className="prose text-[15px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }} />
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <p className="text-[12px] text-ink-3">Only you can see this. Markdown works.</p>
          <span className="ml-auto flex gap-2">
            {initial && (
              <button
                onClick={() => onSave("")}
                disabled={saving}
                className="rounded-md border border-rule px-3 py-1.5 text-[13px] text-ink-2 hover:text-accent disabled:opacity-50"
              >
                Delete
              </button>
            )}
            <button
              onClick={() => onSave(body)}
              disabled={saving || !dirty}
              className="rounded-md bg-accent px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
